import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { Icons } from '@shared/constants';
import { logError } from '@/lib/log';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePushSoftAsk } from '../../hooks/usePushSoftAsk';
import { PushNotificationDisclosureModal } from '../no-spend-weekend/PushNotificationDisclosureModal';
import { openNotificationSettings } from '../../services/shoppingBlockerService';

/**
 * Soft push-permission prompt shown on the paywall's success screen right
 * after a trial starts — a backstop to the HubSpot "your trial ends soon"
 * email (PAID_LAUNCH_PLAN.md §4b/§12). Native only, gated by the same
 * push_soft_ask_enabled flag as the other soft asks, hidden once granted, and
 * never re-shown after an explicit accept or dismiss (recorded on the user
 * doc). Cloned from PracticeNotifyPrompt (daily session's ending screen).
 */
export const TrialNotifyPrompt: React.FC = () => {
  const { currentUser } = useAuth();
  // Unified orchestrator decision — covers the RC flag, native-only, granted/
  // denied permission, cross-surface cooldowns, the lifetime cap and the
  // one-per-session latch (R3 Phase 4).
  const softAsk = usePushSoftAsk('trial');
  const { permissionStatus, requestPermission, updatePreferences } = useNotifications();
  const [showDisclosure, setShowDisclosure] = useState(false);
  // null while the previous-response read is in flight — render nothing until
  // we know this user has never answered.
  const [answered, setAnswered] = useState<boolean | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    getDoc(doc(db, 'userContent', currentUser.uid))
      .then((snap) => {
        if (!cancelled) setAnswered(!!snap.data()?.e_trialNotifyPromptResponse);
      })
      .catch(() => {
        // Read failed — err on the side of not nagging.
        if (!cancelled) setAnswered(true);
      });
    return () => { cancelled = true; };
  }, [currentUser]);

  const visible = softAsk.mayShow && answered === false;

  // Actually showing the ask — claim the one-per-session latch.
  useEffect(() => {
    if (visible) softAsk.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  const recordResponse = async (response: 'accepted' | 'dismissed') => {
    setAnswered(true);
    if (!currentUser) return;
    try {
      await setDoc(doc(db, 'userContent', currentUser.uid), {
        e_trialNotifyPromptResponse: response,
        e_trialNotifyPromptAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      logError(err, { context: 'trial_notify_prompt_record' });
    }
  };

  return (
    <>
      <TrialNotifyBanner
        onNotify={() => setShowDisclosure(true)}
        onDismiss={() => {
          void recordResponse('dismissed');
          void softAsk.recordResponse('dismissed');
        }}
      />

      {showDisclosure && (
        <PushNotificationDisclosureModal
          previouslyDenied={permissionStatus === 'denied'}
          onEnable={async () => {
            setShowDisclosure(false);
            await recordResponse('accepted');
            if (permissionStatus === 'denied') {
              openNotificationSettings();
              void softAsk.recordResponse('accepted');
            } else {
              const granted = await requestPermission();
              void softAsk.recordResponse('accepted', granted ? 'granted' : 'denied');
              // Master toggle only — there is no 'trial' category yet, and the
              // weekly insight anchor belongs to the insight prompt's flow.
              if (granted) await updatePreferences({ enabled: true });
            }
          }}
          onClose={() => setShowDisclosure(false)}
        />
      )}
    </>
  );
};

/** Pure banner view (all copy lives here) — also rendered by the Design Lab for copy review. */
export const TrialNotifyBanner: React.FC<{
  onNotify: () => void;
  onDismiss: () => void;
}> = ({ onNotify, onDismiss }) => (
  <div className="mt-6 w-full rounded-2xl border border-mist bg-cloud px-4 py-3 flex items-center gap-3 text-left">
    <div className="w-9 h-9 rounded-xl bg-foliage/10 flex items-center justify-center shrink-0">
      <Icons.Bell className="w-4 h-4 text-foliage" />
    </div>
    <p className="flex-1 text-sm text-growth/80 leading-snug">
      Get a push notification before your trial ends. 
    </p>
    <button
      type="button"
      onClick={onNotify}
      className="shrink-0 px-3.5 py-2 rounded-xl bg-growth text-cloud text-sm font-semibold"
    >
      Enable
    </button>
    <button
      type="button"
      aria-label="Dismiss"
      onClick={onDismiss}
      className="shrink-0 text-growth/70 p-1 -mr-1"
    >
      <Icons.X className="w-4 h-4" />
    </button>
  </div>
);
